import { ReactNode } from "react";
import { Loader2, AlertCircle, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";

interface PageShellProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}

export const PageShell = ({ description, actions, children }: PageShellProps) => (
  <div className="space-y-6 animate-in fade-in duration-300">
    {/* Page name lives in the top nav bar; keep only description/actions here */}
    {description && (
      <p className="text-sm text-gray-400 text-center max-w-2xl mx-auto px-16 sm:px-20">{description}</p>
    )}
    {actions && <div className="flex flex-wrap items-center justify-center gap-2">{actions}</div>}
    {children}
  </div>
);


export const LoadingState = ({ label = "Loading..." }: { label?: string }) => (
  <div className="flex flex-col items-center justify-center py-20 text-gray-400">
    <Loader2 className="h-7 w-7 animate-spin mb-3" />
    <p className="text-sm">{label}</p>
  </div>
);

export const ErrorState = ({ message, onRetry }: { message?: string; onRetry?: () => void }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <AlertCircle className="h-8 w-8 text-red-400 mb-3" />
    <p className="text-sm text-gray-300 mb-4">{message || "Something went wrong loading this page."}</p>
    {onRetry && <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button>}
  </div>
);

export const EmptyState = ({
  title,
  description,
  icon: Icon = Inbox,
  action,
}: {
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-gray-800 rounded-2xl bg-white/[0.02]">
    <Icon className="h-8 w-8 text-gray-600 mb-3" />
    <p className="font-medium text-white">{title}</p>
    {description && <p className="text-sm text-gray-500 mt-1 max-w-sm">{description}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export default PageShell;
